import { useId, useMemo, useState } from "react";
import { Drawer, Typography } from "@mui/material";
import { CoreSearchField, CoreTable, CoreTableColumn } from "@verity/components/core";
import i18n from "../../../../../i18n";
import noInstalledExtensionsImage from "./assets/no-installed-extensions.png";
import { IncorrectIcon } from "./CollaborationDetection/assets";
import styles from "./InstalledExtensionsModal.module.css";

export function InstalledExtensionsModal({
	open,
	onClose,
	extensions,
}: {
	open: boolean;
	onClose: () => void;
	extensions: string[];
}) {
	const [search, setSearch] = useState("");
	const tableId = useId();
	const titleId = useId();

	const filteredExtensions = useMemo(
		() =>
			extensions
				.filter((extension) => (search ? extension.toLowerCase().includes(search.toLowerCase()) : true))
				.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
		[extensions, search],
	);

	const tableData = useMemo(() => filteredExtensions.map((extension) => ({ name: extension })), [filteredExtensions]);

	const columns: CoreTableColumn<{ name: string }>[] = [
		{
			header: (
				<Typography>
					{i18n.t("result.components.resultsPage.extensionModal.tableHeader", { count: filteredExtensions.length })}
				</Typography>
			),
			key: "name",
			descendingComparator: (a, b) => (a.name ?? "").toLowerCase().localeCompare((b.name ?? "").toLowerCase()),
			cell: (item) => item.data.name,
		},
	];

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			sx={{ zIndex: 1300 }}
			PaperProps={{ className: styles.paper, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId }}
		>
			<div className={styles.header}>
				<Typography id={titleId} variant="h2" className={styles.title}>
					{i18n.t("result.components.resultsPage.extensionModal.title")}
				</Typography>
				<button
					type="button"
					aria-label={i18n.t("result.components.resultsPage.extensionModal.close") as string}
					onClick={onClose}
					className={styles.closeButton}
				>
					<IncorrectIcon width="24px" height="24px" aria-hidden="true" />
				</button>
			</div>
			<div className={styles.container}>
				<Typography className={styles.description}>
					{i18n.t("result.components.resultsPage.extensionModal.description")}
				</Typography>
				{extensions.length === 0 ? (
					<div className={styles.emptyState}>
						<img src={noInstalledExtensionsImage} alt="" />
						<div className={styles.emptyStateText}>
							<Typography variant="h3" className={styles.emptyStateTitle}>
								{i18n.t("result.components.resultsPage.extensionModal.emptyTitle")}
							</Typography>
							<Typography className={styles.emptyStateDescription}>
								{i18n.t("result.components.resultsPage.extensionModal.emptyDescription")}
							</Typography>
						</div>
					</div>
				) : (
					<div className={styles.tableContainer}>
						<CoreSearchField
							ariaControls={tableId}
							label={i18n.t("result.components.resultsPage.extensionModal.search")}
							totalResults={filteredExtensions.length ?? 0}
							onSearch={(value) => setSearch(value)}
							value={search}
							fullWidth
							searchFieldWrapperClassname={styles.searchField}
						/>
						<div className={styles.tableScroll}>
							<CoreTable
								id={tableId}
								aria-label={i18n.t("result.components.resultsPage.extensionModal.title") as string}
								columns={columns}
								data={tableData}
								maxHeight="100%"
								stickyHeader
								noResultsString={i18n.t("result.components.resultsPage.extensionModal.noResults") as string}
								allowUIOverrides
								rowHeight={50}
								rowsPerPage={Math.max(tableData.length, 1)}
								rowProps={{ tabIndex: 0, className: styles.row }}
							/>
						</div>
					</div>
				)}
			</div>
		</Drawer>
	);
}
